<template>
  <v-card flat tile class="documents-container">
    <v-card-text>
      <v-row no-gutters>
        <v-col cols="12">
          <budget-page-header :title="'Documents'" :budgetIterationId="budgetIterationId"></budget-page-header>
        </v-col>
      </v-row>
      <v-row no-gutters>
      </v-row>
      <div class="controls">
        <div class="help-controls" @click="openHelp(0)">
          <span class="help-label">Help</span>
          <v-icon color="var(--v-resBlue-darken1)" class="pb-1" small>mdi-help-circle</v-icon>
        </div>
        <res-dialog :showDialog="showHelp"
                    titleClass="resBlue darken-1 white--text"
                    iconColor="white"
                    cancelButtonText="Close"
                    :cancelButtonOnly="true"
                    cancelButtonColor="resBlue darken-1"
                    :maxWidth="800"
                    :setWidth="800"
                    :setHeight="600"
                    attach=".documents-container"
                    @cancel="closeHelp">
          <documents-help :openSection="openHelpSection"></documents-help>
        </res-dialog>
        <div class="document-controls">
          <res-button class="mr-2"
                      :dark="true"
                      :depressed="true"
                      height="30px"
                      buttonText='+ NEW FOLDER'
                      backgroundColor='var(--v-resGreen-base)'
                      borderColor='var(--v-resGreen-base)'
                      buttonTextColor='white'
                      @click="openAddFolderDocumentDialog"
                      font-size="12px">
          </res-button>
          <res-button :dark="true"
                      :depressed="true"
                      height="30px"
                      buttonText='+ ADD DOCUMENT'
                      backgroundColor='var(--v-resGreen-base)'
                      borderColor='var(--v-resGreen-base)'
                      buttonTextColor='white'
                      @click="openUploadDocumentDialog"
                      font-size="12px">
          </res-button>
        </div>
      </div>
      <div>
        <ag-grid-vue 
          style="width: 100%;"
          class="ag-theme-material"
          :gridOptions="gridOptions"
          :columnDefs="currentHeaders"
          :rowData="currentData"
          @selection-changed="onSelectedFolderChanged">
        </ag-grid-vue>
      </div>
    </v-card-text>
    <add-document-folder-dialog
      v-if="showAddFolderDocumentDialog"
      :showDialog="showAddFolderDocumentDialog"
      @save="addDocumentFolder"
      @cancel="closeAddFolderDocumentDialog">
    </add-document-folder-dialog>
    <upload-document-dialog
      v-if="showUploadDocumentDialog"
      :showDialog="showUploadDocumentDialog"
      @save="uploadDocument"
      @cancel="closeUploadDocumentDialog">
    </upload-document-dialog>
    <edit-document-dialog
      v-if="showEditDocumentDialog"
      :showDialog="showEditDocumentDialog"
      :isFolder="selectedDocumentIsFolder"
      :previousName="selectedDocumentDisplayName"
      @save="editDocument"
      @cancel="closeEditDocumentDialog">
    </edit-document-dialog>
    <confirm-dialog 
      v-if="showDeleteDocumentDialog"
      :showDialog="showDeleteDocumentDialog"
      :message="deleteDocumentsMessage"
      :title="'Delete Documents'"
      @confirmAgree="deleteDocument"
      @cancelConfirm="closeDeleteDocumentDialog">
    </confirm-dialog>
  </v-card>
</template>

<script>
  import ResButton from '@/components/base/ResButton';
  import BudgetPageHeader from '@/components/budgets/common/BudgetPageHeader';
  import { AgGridVue } from "ag-grid-vue";
  import DocumentViewCellRenderer from '@/components/base/ag-grid/DocumentViewCellRenderer';
  import ActionsCellRenderer from '@/components/base/ag-grid/ActionsCellRenderer';
  import AddDocumentFolderDialog from '@/components/budgets/dialogs/AddDocumentFolderDialog.vue';
  import UploadDocumentDialog from '@/components/budgets/dialogs/UploadDocumentDialog.vue';
  import EditDocumentDialog from '@/components/budgets/dialogs/EditDocumentDialog.vue';
  import ConfirmDialog from '@/components/base/dialogs/ConfirmDialog.vue';
  import ResDialog from '@/components/base/ResDialog.vue';

  import DocumentsHelp from '@/components/help/DocumentsHelp.vue';
  import HelpableMixin from '@/mixins/Helpable.js';

  import budgetDocumentsService from '@/services/budget-documents';

  import { mapGetters, mapActions } from 'vuex';

  export default {
    name: 'Documents',
    components: {
      ResButton,
      BudgetPageHeader,
      AgGridVue,
      /* eslint-disable vue/no-unused-components */
      DocumentViewCellRenderer,         // used by AG Grid
      ActionsCellRenderer,              // used by AG Grid
      /* eslint-enable vue/no-unused-components */
      AddDocumentFolderDialog,
      UploadDocumentDialog,
      DocumentsHelp,
      EditDocumentDialog,
      ConfirmDialog,
      ResDialog
    },
    mixins: [HelpableMixin],
    props: {
      budgetIterationId: {
        required: true
      }
    },
    data: function () {
      return {
        showAddFolderDocumentDialog: false,
        showUploadDocumentDialog: false,
        showEditDocumentDialog: false,
        showDeleteDocumentDialog: false,
        selectedBudgetIterationDocument: null,
        availableColumns: [],
        documentData: [],
        gridOptions: {
          headerHeight: 36,
          rowHeight: 36,
          domLayout: 'autoHeight',
          columnDefs: this.availableColumns,
          alignedGrids: [],
          rowSelection: 'single',
          frameworkComponents: {
            'DocumentViewCellRenderer': 'DocumentViewCellRenderer',
            'ActionsCellRenderer': 'ActionsCellRenderer'
          }
        }
      }
    },
    methods: {
      ...mapActions(['changeLoading', 'activateErrorDialog']),
      async load() {
        this.loadGridConfiguration();
        await this.loadDocumentData();
      },
      loadGridConfiguration() {
        let t = this;

        // setup column headers
        t.availableColumns = [
          { headerName: "Name", field: "displayName", width: 500, cellStyle: { textAlign: 'left' }, cellRendererSelector: t.selectDocumentNameCellRenderer },
          { headerName: "Date", field: "attachedDate", width: 100 },
          { headerName: "Size", field: "size", width: 100 },
          { headerName: "Attached By", field: "attachedBy", width: 100 },
          { headerName: "Actions", field: "actions", width: 200, cellRendererSelector: t.selectActionCellRenderer }
        ];
      },
      async loadDocumentData() {
        let t = this;

        let response = await budgetDocumentsService.getBudgetDocuments(t.budgetIterationId);

        t.documentData = [];
        (response.documents).forEach((d) => {
          t.iterateDocuments(d, t.documentData, 0);
        });

        t.setSelectedDocument(null);
        t.gridOptions.api.sizeColumnsToFit();
      },
      iterateDocuments(document, documentsArray, depth) {
        // add document/folder to list
        if (document.isFolder) {
          documentsArray.push(this.initFolderRow(document, depth));
        } else {
          documentsArray.push(this.initDocumentRow(document, depth));
        }

        // continue down the document tree
        if (document.isFolder && document.documents && document.documents.length > 0) {
          for (let i = 0; i < document.documents.length; i++) {
            this.iterateDocuments(document.documents[i], documentsArray, depth + 1);
          }
        }
      },
      initFolderRow(data, depth) {
        let t = this;
        return {
          budgetIterationDocumentId: data.budgetIterationDocumentId,
          parentBudgetIterationDocumentId: data.parentBudgetIterationDocumentId,
          displayName: data.displayName,
          attachedDate: data.attachedDate,
          size: data.size,
          attachedBy: data.attachedBy,
          isFolder: true,
          depth: depth,
          actions: {
            callback: (action, params) => t.gridActionClicked(action, params),
            quickActions: [],
            menuActions: [
              {
                name: 'edit-document',
                icon: null,
                class: null,
                label: 'Edit Name'
              },
              {
                name: 'delete-document',
                icon: null,
                class: null,
                label: 'Delete'
              }
            ]
          }
        };
      },
      initDocumentRow(data, depth) {
        let t = this;
        return {
          budgetIterationDocumentId: data.budgetIterationDocumentId,
          parentBudgetIterationDocumentId: data.parentBudgetIterationDocumentId,
          displayName: data.displayName,
          attachedDate: data.attachedDate,
          size: data.size,
          attachedBy: data.attachedBy,
          isFolder: false,
          depth: depth,
          actions: {
            callback: (action, params) => t.gridActionClicked(action, params),
            quickActions: [
              {
                name: 'view-document',
                icon: 'mdi-eye',
                class: null,
                label: 'View/Download Document'
              }
            ],
            menuActions: [
              {
                name: 'download-document',
                icon: null,
                class: null,
                label: 'View/Download'
              },
              {
                name: 'edit-document',
                icon: null,
                class: null,
                label: 'Edit Name'
              },
              {
                name: 'delete-document',
                icon: null,
                class: null,
                label: 'Delete'
              }
            ]
          }
        };
      },
      selectDocumentNameCellRenderer(params) {
        if (params.data.isFolder == true || params.data.parentBudgetIterationDocumentId) {
          return {
            component: 'DocumentViewCellRenderer',
            params: {
              isFolder: params.data.isFolder,
              isInsideFolder: params.data.parentBudgetIterationDocumentId,
              label: params.data.displayName,
              folderDepth: params.data.depth
            }
          }
        }

        return null; // use default renderer
      },
      selectActionCellRenderer(params) {
        return {
          component: 'ActionsCellRenderer',
          params: { 
            callback: params.data.actions.callback
          }
        }
      },
      onSelectedFolderChanged(event) {
        let selectedNodes = event.api.getSelectedNodes();
        if (selectedNodes.length > 0) {
          this.setSelectedDocument(selectedNodes[0].data);
        } else if (this.selectedBudgetIterationDocument) {
          this.setSelectedDocument(null);
        }
      },
      setSelectedDocument(rowNodeData) {
        this.selectedBudgetIterationDocument = rowNodeData;
        if (!rowNodeData) {
          this.gridOptions.api.deselectAll();
        }
      },
      getSelectedFolderBudgetIterationDocumentId() {
        if (this.selectedBudgetIterationDocument && this.selectedBudgetIterationDocument.isFolder) {
          return this.selectedBudgetIterationDocument.budgetIterationDocumentId;
        }
        return null;
      },
      // add folder logic
      openAddFolderDocumentDialog() {
        this.showAddFolderDocumentDialog = true;
      },
      closeAddFolderDocumentDialog() {
        this.showAddFolderDocumentDialog = false;
        this.setSelectedDocument(null);
      },
      async addDocumentFolder(folderName) {
        this.changeLoading(true);

        let parentBudgetIterationDocumentId = this.getSelectedFolderBudgetIterationDocumentId(); // assign to selected folder (if applicable)
        let response = await budgetDocumentsService.createDocumentFolder(this.budgetIterationId, folderName, parentBudgetIterationDocumentId);
        if (response) {
          this.$eventHub.$emit('showAlertNotification', 'Document folder successfully added!');
          await this.loadDocumentData();
        } else {
          this.activateErrorDialog({ title: 'Budget Documents', message: 'Unable to add document folder' });
        }

        this.closeAddFolderDocumentDialog();
        this.changeLoading(false);
      },
      // upload document logic
      openUploadDocumentDialog() {
        this.showUploadDocumentDialog = true;
      },
      closeUploadDocumentDialog() {
        this.showUploadDocumentDialog = false;
        this.setSelectedDocument(null);
      },
      async uploadDocument(documentToUpload) {
        this.changeLoading(true);

        let parentBudgetIterationDocumentId = this.getSelectedFolderBudgetIterationDocumentId(); // assign to selected folder (if applicable) 
        let response = await budgetDocumentsService.uploadBudgetDocument(this.budgetIterationId, documentToUpload.displayName, parentBudgetIterationDocumentId, documentToUpload.file);
        if (response) {
          this.$eventHub.$emit('showAlertNotification', 'Document successfully uploaded!');
          await this.loadDocumentData();
        } else {
          this.activateErrorDialog({ title: 'Budget Documents', message: 'Unable to upload document' });
        }

        this.closeUploadDocumentDialog();
        this.changeLoading(false);
      },
      // edit document logic
      openEditDocumentDialog(rowNodeData) {
        this.showEditDocumentDialog = true;
        this.setSelectedDocument(rowNodeData);
      },
      closeEditDocumentDialog() {
        this.showEditDocumentDialog = false;
        this.setSelectedDocument(null);
      },
      async editDocument(displayName) {
        this.changeLoading(true);

        let budgetIterationDocumentId = this.selectedBudgetIterationDocument.budgetIterationDocumentId;
        let parentBudgetIterationDocumentId = this.selectedBudgetIterationDocument.parentBudgetIterationDocumentId;
        let response = await budgetDocumentsService.updateBudgetDocument(this.budgetIterationId, budgetIterationDocumentId, displayName, parentBudgetIterationDocumentId);
        if (response) {
          this.$eventHub.$emit('showAlertNotification', this.selectedDocumentIsFolder ? 'Folder successfully edited!' : 'Document successfully edited!');
          await this.loadDocumentData();
        } else {
          this.activateErrorDialog({ title: 'Budget Documents', message: 'Unable to edit document' });
        }

        this.closeEditDocumentDialog();
        this.changeLoading(false);
      },
      // delete document logic
      openDeleteDocumentDialog(rowNodeData) {
        this.showDeleteDocumentDialog = true;
        this.setSelectedDocument(rowNodeData);
      },
      closeDeleteDocumentDialog() {
        this.showDeleteDocumentDialog = false;
        this.setSelectedDocument(null);
      },
      async deleteDocument() {
        this.changeLoading(true);

        let response = await budgetDocumentsService.deleteBudgetDocument(this.budgetIterationId, this.selectedBudgetIterationDocument.budgetIterationDocumentId);
        if (response) {
          this.$eventHub.$emit('showAlertNotification', 'Document(s) successfully deleted!');
          this.loadDocumentData();
        } else {
          this.activateErrorDialog({ title: 'Budget Documents', message: 'Unable to delete document(s)' });
        }

        this.closeDeleteDocumentDialog();
        this.changeLoading(false);
      },
      // view/download document logic
      async viewDocument(rowNodeData) {
        let response = await budgetDocumentsService.getBudgetDocumentUri(this.budgetIterationId, rowNodeData.budgetIterationDocumentId);
        if (response) {
          window.open(response.uri, '_blank');
        } else {
          this.activateErrorDialog({ title: 'Budget Documents', message: 'Unable to retrieve document' });
        }

        this.setSelectedDocument(null);
      },
      // grid options
      async gridActionClicked(action, params) {
        switch(action.name) {
          case 'view-document':
          case 'download-document':
            this.viewDocument(params.data);
            break;
          case 'edit-document':
            this.openEditDocumentDialog(params.data);
            break;
          case 'delete-document':
            this.openDeleteDocumentDialog(params.data);
            break;
        }
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      currentData() {
        return this.documentData;
      },
      currentHeaders() { 
        return this.availableColumns;
      },
      selectedDocumentIsFolder() {
        return this.selectedBudgetIterationDocument && this.selectedBudgetIterationDocument.isFolder == true;
      },
      selectedDocumentDisplayName() {
        return this.selectedBudgetIterationDocument ? this.selectedBudgetIterationDocument.displayName : null;
      },
      deleteDocumentsMessage() {
        return this.selectedDocumentIsFolder ? 
          'Are you sure you want to delete this folder and all of its contents?' : 
          'Are you sure you want to delete this file?';
      }
    },
    mounted() {
      this.changeLoading(true);
      this.load();
      this.changeLoading(false);
    },
    created() {
    }
  }
</script>

<style lang="scss" scoped>
  .controls {
    height: 40px;
    margin-bottom: 14px;
  }

  .document-controls {
    height: 100%;
    float: right;
    border: solid 1px var(--v-resGrey-lighten3);
    vertical-align: center; 
    padding: 4px;
    margin-right: 8px;
  }

  .help-controls {
    height: 100%;
    float: right;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 9px 6px 6px 6px;
    cursor: pointer !important;
  }

  .help-label {
    padding-right: 6px;
  }
</style>
